import React, { useState, useCallback, useEffect } from 'react'
import { Form, Button, Checkbox } from 'antd'
import { useSelector, useDispatch } from 'react-redux'
import Actions from '../../../../actions'
import GreenCheckMark from '../../../../resources/GreenCheckMark.png'
import RedCrossMark from '../../../../resources/RedCrossMark.png'
import $t from '../../../../i18n'
import {
  FormFields,
  passwordRules as passwordRulesList,
  passwordRuleTitleConsts,
  signUpFlowTypes,
} from '../../../../consts'
import config from '../../../../config.json'
import { event, eventTypes } from '../../../../consts/eventConsts'
import MaterialInput from '../../../common/MaterialInput'
import Page from '../../../common/Page/Page'
import PageCard from '../../../common/PageCard'

import './ChoosePassword.css'

const ChoosePassword = () => {
  const [form] = Form.useForm()
  const { getFieldValue } = form
  const dispatch = useDispatch()

  const userSignup = useSelector(state => state.signup?.userSignup)
  const pendingPatient = useSelector(state => state.signup?.pendingPatient)
  const signupLoading = useSelector(state => state.signup?.signupLoading)
  const prefillFieldsForSignup = useSelector(
    state => state.signup?.prefillFieldsForSignup
  )
  const signupErrorMessage = useSelector(
    state => state.signup?.signupErrorMessage
  )
  const signupFlowType = useSelector(state => state.auth?.signupFlowType)

  const [shouldStartDynamicValidation, setShouldStartDynamicValidation] =
    useState(false)
  const [passwordRules, setPasswordRules] = useState(passwordRulesList)
  const [isValidPassword, setIsValidPassword] = useState(false)
  const [isValidReEnterPassword, setIsValidReEnterPassword] = useState(false)

  const isConsentAgreed = useSelector(
    state => state.app?.isHmoPrivacyConcentAgreed
  )

  // check if the flow is HMO or the user is signing up with prefill fields (from cms)
  const isHmoFlow =
    signupFlowType === signUpFlowTypes.HMO || !!prefillFieldsForSignup

  useEffect(() => {
    const updatedRules = [...passwordRules]
    if (signupErrorMessage && typeof signupErrorMessage !== 'string') {
      signupErrorMessage.forEach(message => {
        if (!updatedRules.find(eachRule => eachRule.text === message)) {
          updatedRules.push({
            isValid: false,
            text: message,
            title: passwordRuleTitleConsts.BE_ERROR,
          })
        }
      })
    } else if (signupErrorMessage && typeof signupErrorMessage === 'string') {
      if (
        !updatedRules.find(eachRule => eachRule.text === signupErrorMessage)
      ) {
        updatedRules.push({
          isValid: false,
          text: signupErrorMessage,
          title: passwordRuleTitleConsts.BE_ERROR,
        })
      }
    }
    setPasswordRules([...updatedRules])
  }, [signupErrorMessage])

  const handleSignup = useCallback(data => {
    dispatch(
      Actions.createAction(Actions.SIGNUP_PASSWORD_REQUESTED, {
        password: data.password,
        userSignup,
        pendingPatient,
      })
    )
  }, [])

  const validateReEnterPassword = (rule, value, callback) => {
    const password = getFieldValue(FormFields.password)

    if (value !== password) {
      setIsValidReEnterPassword(false)
      callback($t('pages.getStarted.signupForm.errors.passwordDontMatch'))
    } else {
      setIsValidReEnterPassword(true)
      callback()
    }
  }

  const validatePassword = (rule, value, callback) => {
    const rules = [...passwordRules]
    const numberFormat = /\d/
    const lowerCaseAlphabetFormat = /[a-z]/
    const upperCaseAlphabetFormat = /[A-Z]/
    const specialCharacterFormat = /[~!@#$%^&*_+=`|\\(){}[\]:;'<>,.?/-]/
    const inValidRuleTitles = []

    if (!shouldStartDynamicValidation) {
      setShouldStartDynamicValidation(true)
    }

    if (!numberFormat.test(value)) {
      inValidRuleTitles.push(passwordRuleTitleConsts.NUMBER)
    }
    if (!lowerCaseAlphabetFormat.test(value)) {
      inValidRuleTitles.push(passwordRuleTitleConsts.SMALL_LETTER)
    }
    if (!upperCaseAlphabetFormat.test(value)) {
      inValidRuleTitles.push(passwordRuleTitleConsts.CAPITAL_LETTER)
    }
    if (!specialCharacterFormat.test(value)) {
      inValidRuleTitles.push(passwordRuleTitleConsts.SPECIAL_CHARACTER)
    }
    if (value.length <= 7) {
      inValidRuleTitles.push(passwordRuleTitleConsts.LENGTH)
    }
    if (value === '') {
      callback($t('pages.getStarted.signupForm.errors.pleaseInputYourPassword'))
    } else {
      callback()
    }

    const updatedPasswordRules = rules.reduce((updatedRules, rule) => {
      if (inValidRuleTitles.includes(rule.title)) {
        updatedRules.push({ ...rule, isValid: false })
      } else if (rule.title === passwordRuleTitleConsts.BE_ERROR) {
        updatedRules.push({ ...rule, isValid: false })
      } else {
        updatedRules.push({ ...rule, isValid: true })
      }
      return updatedRules
    }, [])

    if (inValidRuleTitles.length === 0 && !isValidPassword) {
      setIsValidPassword(true)
    } else if (inValidRuleTitles.length && isValidPassword) {
      setIsValidPassword(false)
    }
    setPasswordRules([...updatedPasswordRules])
  }

  const renderPasswordRules = () => {
    return (
      <div className="password-rules-container">
        <div className="password-rules-title">
          {$t('pages.getStarted.signupForm.passwordRules.passwordMustInclude')}
        </div>
        {passwordRules.map(rule => {
          return (
            <div className="rule-continer">
              {shouldStartDynamicValidation ? (
                <img
                  alt=""
                  className="validation-rule-icon"
                  src={rule.isValid ? GreenCheckMark : RedCrossMark}
                />
              ) : null}
              <span
                className={
                  shouldStartDynamicValidation
                    ? rule.isValid
                      ? 'valid-rule-text'
                      : 'invalid-rule-text'
                    : 'rule-text'
                }
              >
                {rule.text}
              </span>
            </div>
          )
        })}
      </div>
    )
  }

  // in case should show consent is true and the user did consent to terms
  // the flag sent from signup flow only
  const isUserConsentedTerms = isHmoFlow ? isConsentAgreed : true

  const shouldEnableNextButton = () =>
    isValidPassword && isValidReEnterPassword && isUserConsentedTerms

  return (
    <PageCard className="login-page-card change-password-page-card">
      <Page
        title={$t('pages.getStarted.signupForm.chooseAPassword')}
        className="login-page choose-password-page"
      >
        <Form
          className="password-form"
          onFinish={handleSignup}
          onFinishFailed={error => {
            dispatch(
              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                type: eventTypes.formEvent,
                eventName: event.formEvent.invalid,
                data: {},
                error: error,
              })
            )
          }}
          name="basic"
          initialValues={{
            password: '',
            reEnterPassword: '',
          }}
          form={form}
        >
          <Form.Item
            className={'form-item choose-password-form-item'}
            name={FormFields.password}
            type="text"
            rules={[{ validator: validatePassword }]}
          >
            <MaterialInput
              className="choose-password-form-input"
              type="password"
              label={$t('pages.getStarted.signupForm.password')}
            />
          </Form.Item>

          <Form.Item
            className="form-item choose-password-form-item confirm-password-form-item"
            name={FormFields.reEnterPassword}
            type="text"
            rules={[{ validator: validateReEnterPassword }]}
          >
            <MaterialInput
              className="re-enter-password"
              type="password"
              label={$t('pages.getStarted.signupForm.reEnterPassword')}
            />
          </Form.Item>
          {renderPasswordRules()}
          <div className="choose-password-footer-container">
            <Form.Item>
              {isHmoFlow && (
                <Checkbox
                  checked={isConsentAgreed}
                  className="hmo-consent-checkbox"
                  onChange={() =>
                    dispatch(
                      Actions.createAction(
                        Actions.SHOW_HMO_PRIVACY_CONCENT_DIALOG
                      )
                    )
                  }
                >
                  <div className="terms-privacy-hmo">
                    {$t('dialogs.consentHMO.termsAndPrivacy')}{' '}
                    <span
                      className="link"
                      onClick={() =>
                        window.open(config.privacyPraticesURL, '_blank')
                      }
                    >
                      {$t('dialogs.consentHMO.termsAndPrivacyLink')}
                    </span>
                  </div>
                </Checkbox>
              )}
              <Button
                loading={signupLoading}
                className="signup-next-button"
                htmlType="submit"
                type="primary"
                disabled={!shouldEnableNextButton()}
              >
                {$t('pages.getStarted.signupForm.approveTermsAndContinue')}
              </Button>
            </Form.Item>
          </div>
        </Form>
      </Page>
    </PageCard>
  )
}

export default ChoosePassword
